import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { ASSET_MOVE_CONFLICT_CODE } from '@/api/constants';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getMoveConflictMessage,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { FormDateTimePicker } from '@/components/form-date-time-picker';
import { FormTextField } from '@/components/form-text-field';
import { SupportAlert } from '@/components/support-alert';
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { invalidateInventory } from '@/inventory/utilities/invalidate-inventory';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import {
  ReceiveShipmentRequest,
  receiveShipment,
} from '@/shipment/api/receive-shipment';
import { Shipment } from '@/shipment/api/types';
import {
  UpdateShipmentRequest,
  updateShipment,
} from '@/shipment/api/update-shipment';
import { validateSerials } from '@/shipment/api/validate-serials';
import { ReceiveConfirmationDialog } from '@/shipment/components/receive-confirmation-dialog';
import { ReceiveDialogExistingItem } from '@/shipment/components/receive-dialog-existing-item';
import { ReceiveDialogNewItem } from '@/shipment/components/receive-dialog-new-item';
import { SerialCollectionDrawer } from '@/shipment/components/serial-collection-drawer';
import { SerialValidationModal } from '@/shipment/components/serial-validation-modal';
import { shipmentDetailQueryKey } from '@/shipment/hooks/use-shipment';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import { shipmentLineAllowsSerials } from '@/shipment/utilities/shipment-serials';
import {
  SparseItem,
  sparseItemSchema,
  sparseLocationSchema,
} from '@/sparse/api/types';
import { extractSparseItem } from '@/sparse/utilities/to-sparse-item';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useCallback, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

const receiveItemSchema = z.object({
  item: sparseItemSchema.nullable(),
  quantity: z
    .number()
    .min(0, {
      message: 'Must be zero or more',
    })
    .nullable(),
  initialQuantity: z.number().nullable(),
  externalReference: z.string().nullable(),
  serials: z.array(z.string()).optional(),
  isOriginalItem: z.boolean().optional(),
});

const receiveFormSchema = z
  .object({
    receiveWarehouse: sparseLocationSchema.nullable(),
    items: z.array(receiveItemSchema),
    receivedAt: dateTimeSchema.nullable(),
    receiveNote: z.string().nullable().optional(),
  })
  .superRefine((data, ctx) => {
    // Run validation on submission so we can still have null as a state in the form.
    // The nulls are legal till the form is submitted then errors should be shown.

    // Check if toLocation is set (not null)
    if (!data.receiveWarehouse) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Warehouse location is required',
        path: ['receiveWarehouse'],
      });
    }

    // Check if receivedAt is set (not null)
    if (!data.receivedAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Receive date is required',
        path: ['receivedAt'],
      });
    }

    // Check if items array has at least one item
    if (data.items && data.items.length > 0) {
      // Check that every item has its item property not null
      data.items.forEach((item, index) => {
        if (!item.item) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Item is required',
            path: ['items', index, 'item'],
          });
        }
        if (item.quantity === null || item.quantity < 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Must be zero or more',
            path: ['items', index, 'quantity'],
          });
        }

        // Strict 1:1 serial:quantity only applies to tracked devices; bulk is lenient.
        const requiresStrictSerialCount =
          item.item?.hasAssets && item.item?.isPhysical;

        if (
          requiresStrictSerialCount &&
          item.quantity !== null &&
          item.quantity > 0
        ) {
          const serialCount = item.serials?.length ?? 0;
          if (serialCount !== item.quantity) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Serials required: ${serialCount} entered, ${item.quantity} needed`,
              path: ['items', index, 'serials'],
            });
          }
        }
      });
    }
  });

export type ReceiveDialogFormData = z.infer<typeof receiveFormSchema>;

/** Builds the receive form schema, refusing a new receipt dated before the shipment's delivery, or its ship time when it has no delivery. */
function createReceiveFormSchema(shipment: Shipment | undefined) {
  return receiveFormSchema.superRefine((data, ctx) => {
    // An existing receipt is edited with its date locked, so only a new receipt is held to the order.
    if (!data.receivedAt || shipment?.receivedAt) {
      return;
    }

    if (shipment?.deliveredAt && data.receivedAt < shipment.deliveredAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Receive date cannot be before the delivery date',
        path: ['receivedAt'],
      });
    } else if (
      !shipment?.deliveredAt &&
      shipment?.shippedAt &&
      data.receivedAt < shipment.shippedAt
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Receive date cannot be before the ship date',
        path: ['receivedAt'],
      });
    }
  });
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  shipment?: Shipment;
}

// Internal component that gets remounted when shipment changes
function ReceiveDialogForm(props: Props) {
  const queryClient = useQueryClient();
  const isEditMode = !!props.shipment?.receivedAt;
  const confirmationDialog = useDisclosure();
  const dialogRef = useRef<HTMLDivElement>(null);

  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  // Kept apart from formErrorMessage: a move conflict is the user's to fix, so it must
  // not render through SupportAlert, which tells them support has been notified
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  const [pendingFormData, setPendingFormData] =
    useState<ReceiveDialogFormData | null>(null);
  const [serialDrawerOpen, setSerialDrawerOpen] = useState<number | null>(null);
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  // Track globally invalid serials (not product-specific, as serials are globally unique per org)
  const [globalInvalidSerials, setGlobalInvalidSerials] = useState<Set<string>>(
    new Set()
  );

  const getDefaultValues = useCallback(() => {
    if (isEditMode && props.shipment) {
      return {
        items: props.shipment.items.map((lineItem) => ({
          item: extractSparseItem(lineItem),
          externalReference: lineItem.externalReference,
          initialQuantity: lineItem.quantity,
          quantity: lineItem.receivedQuantity ?? lineItem.quantity,
          serials: shipmentLineAllowsSerials(lineItem.item)
            ? (lineItem.serials ?? [])
            : [],
          isOriginalItem: true,
        })),
        receiveNote: props.shipment.receiptNote,
        receiveWarehouse: props.shipment.toLocation,
        receivedAt: props.shipment.receivedAt,
      };
    }
    return {
      items:
        props.shipment?.items.map((lineItem) => ({
          item: extractSparseItem(lineItem),
          externalReference: null,
          initialQuantity: lineItem.quantity,
          quantity: lineItem.quantity,
          serials: [],
          isOriginalItem: true,
        })) || [],
      receiveNote: null,
      receiveWarehouse: props.shipment?.toLocation ?? null,
      receivedAt: DateTime.now().set({ second: 0, millisecond: 0 }),
    };
  }, [isEditMode, props.shipment]);

  const {
    control,
    reset,
    handleSubmit,
    watch,
    setValue,
    formState,
    clearErrors,
  } = useForm<ReceiveDialogFormData>({
    resolver: zodResolver(createReceiveFormSchema(props.shipment)),
    defaultValues: getDefaultValues(),
  });

  const {
    fields: itemsField,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'items',
  });

  const handleClose = () => {
    resetFormToOriginal();
    setFormErrorMessage(null);
    setConflictMessage(null);
    setPendingFormData(null);
    confirmationDialog.close();
    props.onClose();
  };

  const handleSuccessfulSubmit = () => {
    setFormErrorMessage(null);
    setPendingFormData(null);
    confirmationDialog.close();
    if (props.shipment) {
      queryClient.invalidateQueries({
        queryKey: shipmentDetailQueryKey(props.shipment.key),
      });
    }
    queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
    invalidateInventory(queryClient);
    props.onClose();
    // Reset form after dialog is closed
    setTimeout(() => {
      resetFormToOriginal();
    }, 0);
  };

  // A move conflict is actionable by the user, so it renders inline and is not sent to
  // Sentry. Everything else keeps the existing generic treatment
  const handleMutationError = (error: AxiosError) => {
    if (isPlanLimitError(error)) {
      return;
    }

    const apiError = handleAPIError(error);
    if (apiError.apiStatusCode === ASSET_MOVE_CONFLICT_CODE) {
      setConflictMessage(getMoveConflictMessage(apiError.conflictingAssets));
      setFormErrorMessage(null);
      return;
    }
    // A refusal is the shipment telling the user it is no longer theirs to
    // change, which is actionable rather than a fault, so it renders inline and
    // is not reported.
    const curated = getUserFacingErrorMessage(apiError);
    if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
      setConflictMessage(null);
      setFormErrorMessage(curated);
      return;
    }
    // The API error handler already reports 500s and auth failures, so only capture what it did not
    if (!apiError.sentToSentry) {
      captureException(error);
    }
    setConflictMessage(null);
    setFormErrorMessage('An error occurred. Please try again.');
  };

  const receiveShipmentMutation = useMutation({
    mutationFn: (data: ReceiveShipmentRequest) =>
      receiveShipment(props.shipment?.key ?? '', data),
    onSuccess: handleSuccessfulSubmit,
    onError: handleMutationError,
  });

  const updateShipmentMutation = useMutation({
    mutationFn: (data: UpdateShipmentRequest) =>
      updateShipment(props.shipment?.key ?? '', data),
    onSuccess: handleSuccessfulSubmit,
    onError: handleMutationError,
  });

  const onSubmit = async (data: ReceiveDialogFormData) => {
    setFormErrorMessage(null);
    setConflictMessage(null);

    if (isEditMode) {
      // For edit mode, skip confirmation and directly update
      const items = data.items.map((item) => {
        const originalItem = props.shipment?.items.find(
          (shipmentItem) => shipmentItem.id === item.item?.id
        );
        return {
          itemId: item.item?.id ?? '',
          quantity:
            originalItem?.receivedQuantity ?? originalItem?.quantity ?? 0,
          externalReference: item.externalReference ?? undefined,
        };
      });

      return updateShipmentMutation.mutate({
        receivedItems: items,
        receiptNote: data.receiveNote ?? null,
      });
    }

    // For create mode, check if there are device items that need serial validation
    const productItems = data.items.filter(
      (item) => item.item?.hasAssets && item.item?.isPhysical
    );

    if (productItems.length === 0) {
      // No products, proceed directly to confirmation dialog
      setPendingFormData(data);
      confirmationDialog.open();
      return;
    }

    // Validate serials for products
    // For return order shipments, serials must already exist in the system
    const isReturnOrderShipment = !!props.shipment?.returnOrder;
    setValidationModalOpen(true);
    const startTime = performance.now();

    try {
      const validationResponse = await validateSerials({
        items: productItems.map((item) => ({
          productId: item.item?.id ?? '',
          serials: item.serials ?? [],
        })),
        requireExisting: isReturnOrderShipment,
      });

      const elapsed = performance.now() - startTime;
      const remaining = Math.max(0, 400 - elapsed);

      setTimeout(() => {
        setValidationModalOpen(false);

        // Accumulate invalid serials globally (they persist across validations)
        const newInvalidSerials = new Set(globalInvalidSerials);
        let hasInvalidSerials = false;

        for (const validationItem of validationResponse.items) {
          if (validationItem.invalidSerials.length > 0) {
            hasInvalidSerials = true;
            validationItem.invalidSerials.forEach((serial) => {
              newInvalidSerials.add(serial);
            });
          }
        }

        // Update global invalid serials set
        setGlobalInvalidSerials(newInvalidSerials);

        if (hasInvalidSerials) {
          // There are invalid serials, stay on receive dialog and show errors
          // Don't clear existing invalid serials, just add new ones
        } else {
          // All serials are valid, proceed to confirmation dialog
          setPendingFormData(data);
          confirmationDialog.open();
        }
      }, remaining);
    } catch (error) {
      setValidationModalOpen(false);
      captureException(error);
      setFormErrorMessage(
        'An error occurred during validation. Please try again.'
      );
    }
  };

  const handleConfirmReceipt = () => {
    if (!pendingFormData) return;

    confirmationDialog.close();

    const items = pendingFormData.items.map((item) => ({
      itemId: item.item?.id ?? '',
      quantity: item.quantity ?? -1,
      externalReference: item.externalReference ?? undefined,
      serials: shipmentLineAllowsSerials(item.item)
        ? (item.serials ?? [])
        : undefined,
    }));

    // Ensure seconds and milliseconds are 0
    const receivedAt = pendingFormData.receivedAt!.set({
      second: 0,
      millisecond: 0,
    });
    const timezone = receivedAt.zoneName;
    receiveShipmentMutation.mutate({
      items: items,
      receiptNote: pendingFormData.receiveNote ?? undefined,
      receiveLocationId: pendingFormData.receiveWarehouse?.id ?? '',
      receivedAt: receivedAt.toUTC(),
      receivedAtTimezone: timezone,
    });
  };

  // Watch all items to exclude already selected ones from the dropdown
  const watchedItems = watch('items');
  const getHiddenItemsForIndex = (currentIndex: number): SparseItem[] => {
    return (
      watchedItems
        ?.map((item, index) => {
          if (index === currentIndex || !item.item) return null;
          return item.item;
        })
        .filter((item): item is SparseItem => item !== null) || []
    );
  };

  const addNewItem = () => {
    append({
      item: null,
      quantity: 1,
      initialQuantity: null,
      externalReference: null,
      serials: [],
      isOriginalItem: false, // Mark as newly added item
    });
  };

  const resetFormToOriginal = () => {
    // Reset to original shipment items only (remove any added items)
    if (props.shipment) {
      reset({
        items: props.shipment.items.map((lineItem) => ({
          item: extractSparseItem(lineItem),
          externalReference: null,
          initialQuantity: lineItem.quantity,
          quantity: lineItem.quantity,
          serials: [],
          isOriginalItem: true,
        })),
        receiveNote: null,
        receiveWarehouse: props.shipment?.toLocation ?? null,
      });
    } else {
      reset();
    }
  };

  // Get item summaries for confirmation dialog
  const getItemSummariesForConfirmation = (data: ReceiveDialogFormData) => {
    return data.items
      .filter((item) => item.item !== null)
      .map((item) => ({
        item: {
          id: item.item!.id,
          sku: item.item!.sku,
          name: item.item!.name,
          hasAssets: item.item!.hasAssets,
        },
        receivedQuantity: item.quantity ?? 0,
        expectedQuantity: item.initialQuantity,
      }));
  };

  return (
    <>
      {/* Validation Modal - shown during serial validation */}
      <SerialValidationModal isOpen={validationModalOpen} />

      {/* Confirmation Dialog - shown on top of the receive dialog */}
      {props.shipment && pendingFormData && (
        <ReceiveConfirmationDialog
          isOpen={confirmationDialog.isOpen}
          onClose={confirmationDialog.close}
          onConfirm={handleConfirmReceipt}
          shipment={props.shipment}
          itemsToReceive={getItemSummariesForConfirmation(pendingFormData)}
        />
      )}

      <Dialog
        ref={dialogRef}
        open={props.isOpen}
        onClose={handleClose}
        fullWidth
        maxWidth={serialDrawerOpen !== null ? 'lg' : 'sm'}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(onSubmit),
          sx: {
            display: 'flex',
            flexDirection: 'row',
            height: '90vh',
            maxHeight: '90vh',
            transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <DialogTitle sx={{ px: 3, pt: 3 }}>
            {isEditMode ? 'Receipt details' : 'Mark shipment as received'}
          </DialogTitle>
          <DialogContent sx={{ px: 3, pb: 1 }}>
            <Stack gap={3}>
              {conflictMessage && (
                <Alert
                  severity="error"
                  onClose={() => setConflictMessage(null)}
                >
                  {conflictMessage}
                </Alert>
              )}
              {formErrorMessage && <SupportAlert message={formErrorMessage} />}
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {`Record receipt details for shipment ${props.shipment?.shipmentNumber}`}
                </Typography>
              </Box>
              <ConditionalTooltip
                arrow
                tooltipText={
                  isEditMode
                    ? 'Location cannot be modified once units have been received'
                    : undefined
                }
                sx={{ width: '100%' }}
              >
                <Box sx={{ width: '100%' }}>
                  <FormLocationSearch
                    name={'receiveWarehouse'}
                    control={control}
                    label="Receiving location"
                    disabled={isEditMode}
                    onCreate={(name: string) => {
                      const url = getNewLocationURL(name);
                      window.open(url, '_blank');
                    }}
                  />
                </Box>
              </ConditionalTooltip>
              <FormDateTimePicker
                disableFuture
                name="receivedAt"
                label="Receive date"
                control={control}
                isDisabled={isEditMode}
                minimumDate={
                  props.shipment?.deliveredAt ??
                  props.shipment?.shippedAt ??
                  undefined
                }
              />
              <Stack gap={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ pl: 1 }}
                >
                  <Typography variant="subtitle2">Items</Typography>
                  {!props.shipment?.receivedAt && (
                    <Button
                      type="button"
                      size="small"
                      startIcon={<AddRounded />}
                      onClick={addNewItem}
                      variant="outlined"
                    >
                      Add item
                    </Button>
                  )}
                </Stack>
                <Stack gap={2}>
                  {itemsField.map((field, index) => {
                    const watchedItem = watchedItems?.[index];
                    // Filter global invalid serials to only those in this item
                    const itemInvalidSerials =
                      watchedItem?.serials && globalInvalidSerials.size > 0
                        ? new Set(
                            watchedItem.serials.filter((serial) =>
                              globalInvalidSerials.has(serial)
                            )
                          )
                        : undefined;

                    return isEditMode || field.isOriginalItem ? (
                      <ReceiveDialogExistingItem
                        key={field.id}
                        index={index}
                        item={field}
                        watchedItem={watchedItem}
                        control={control}
                        formState={formState}
                        isEditMode={isEditMode}
                        onSerialDrawerOpen={setSerialDrawerOpen}
                        serialDrawerOpen={serialDrawerOpen}
                        onClearErrors={(idx) => {
                          clearErrors(`items.${idx}.serials`);
                        }}
                        invalidSerials={itemInvalidSerials}
                      />
                    ) : (
                      <ReceiveDialogNewItem
                        key={field.id}
                        index={index}
                        item={field}
                        watchedItem={watchedItem}
                        control={control}
                        formState={formState}
                        hiddenItems={getHiddenItemsForIndex(index)}
                        onRemove={remove}
                        onSerialDrawerOpen={setSerialDrawerOpen}
                        serialDrawerOpen={serialDrawerOpen}
                        onClearErrors={(idx) => {
                          clearErrors(`items.${idx}.serials`);
                        }}
                        invalidSerials={itemInvalidSerials}
                      />
                    );
                  })}
                </Stack>
              </Stack>
              <ConditionalTooltip
                arrow
                tooltipText={
                  isEditMode
                    ? 'Notes cannot be modified after receipt confirmation'
                    : undefined
                }
                sx={{ width: '100%' }}
              >
                <Box sx={{ width: '100%' }}>
                  <FormTextField
                    fullWidth
                    isMultiline
                    minimumRows={3}
                    name="receiveNote"
                    label="Notes (optional)"
                    placeholder={'Add any notes about this receipt'}
                    control={control}
                    disabled={isEditMode}
                  />
                </Box>
              </ConditionalTooltip>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
            {isEditMode ? (
              <Button type="button" onClick={handleClose} variant="outlined">
                Close
              </Button>
            ) : (
              <>
                <Button type="button" onClick={handleClose} variant="outlined">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={receiveShipmentMutation.isPending}
                >
                  Confirm receipt
                </Button>
              </>
            )}
          </DialogActions>
        </Box>

        {/* Serial Collection Drawer */}
        {serialDrawerOpen !== null &&
          (() => {
            const currentItem = watchedItems?.[serialDrawerOpen];
            // Filter global invalid serials to only those in this item
            const drawerInvalidSerials =
              currentItem?.serials && globalInvalidSerials.size > 0
                ? new Set(
                    currentItem.serials.filter((serial) =>
                      globalInvalidSerials.has(serial)
                    )
                  )
                : undefined;

            // Build global serials set from all items EXCEPT the current one
            const globalSerialsSet = new Set<string>();
            watchedItems?.forEach((item, index) => {
              if (index !== serialDrawerOpen && item?.serials) {
                item.serials.forEach((serial) => globalSerialsSet.add(serial));
              }
            });

            return (
              <SerialCollectionDrawer
                isOpen={true}
                onClose={() => setSerialDrawerOpen(null)}
                itemId={itemsField[serialDrawerOpen]?.id ?? ''}
                partSku={currentItem?.item?.sku || ''}
                partName={currentItem?.item?.name ?? ''}
                receivedQuantity={currentItem?.quantity ?? 0}
                serials={currentItem?.serials ?? []}
                onSerialsChange={(serials) => {
                  if (serialDrawerOpen !== null) {
                    setValue(`items.${serialDrawerOpen}.serials`, serials, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: false,
                    });
                    clearErrors(`items.${serialDrawerOpen}.serials`);
                  }
                }}
                invalidSerials={drawerInvalidSerials}
                globalSerials={globalSerialsSet}
                readOnly={isEditMode}
              />
            );
          })()}
      </Dialog>
    </>
  );
}

// Creates a stable fingerprint of shipment items that changes when any item data changes.
// This ensures the form remounts with fresh state when items are added, removed, or modified.
function getItemsFingerprint(items: Shipment['items']): string {
  return items
    .map((lineItem) => {
      const serials = lineItem.serials ? lineItem.serials.join('|') : '';
      return `${lineItem.id}:${lineItem.quantity}:${lineItem.receivedQuantity}:${lineItem.externalReference}:${serials}`;
    })
    .join(',');
}

// Wrapper component that forces remount when shipment changes
export function ReceiveDialog(props: Props) {
  // Dialog isn't valid without a shipment
  if (!props.shipment) {
    return null;
  }

  // Force complete remount when shipment key, receivedAt, or items change to ensure clean state.
  // The items fingerprint captures all relevant item data (type, id, quantity, receivedQuantity,
  // externalReference, serials) so any change to any item property triggers a remount.
  const itemsFingerprint = getItemsFingerprint(props.shipment.items);
  const componentKey = `${props.shipment.key}-${props.shipment.receivedAt?.toMillis() || 'not-received'}-${itemsFingerprint}`;
  return <ReceiveDialogForm key={componentKey} {...props} />;
}
